import { Box, Divider, FlexLayout, H4, Icon } from '@deity/falcon-ui';
import { BaseHelmet, CreateEditEndpointModal, EntityDetailsHeader } from 'components';
import { APOLLO_BASE_OPTIONS, CACHE_FIRST } from 'contracts/constants';
import type { FC } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetEndpointQuery, useGetEntityUsageInfoQuery } from 'services/graphql/generated/hooks';

type EndpointOverviewProps = {
  params: any;
  isEditable?: boolean;
};

export const EndpointOverview: FC<EndpointOverviewProps> = ({ params, isEditable }) => {
  const [endpointModalOpen, setEndpointModalOpen] = useState<boolean>(false);
  const {
    loading: loadingEndpoint,
    data: endpointData,
    refetch
  } = useGetEndpointQuery({
    variables: { packageVersionUuid: params.versionUuid, uuid: params.entityId },
    ...CACHE_FIRST
  });

  const {
    loading: loadingInfo,
    data: infoData,
    refetch: refetchInfo
  } = useGetEntityUsageInfoQuery({
    variables: {
      versionUuid: params.versionUuid,
      entityType: 'endpoints',
      entityKey: params.entityId
    },
    skip: !(params.versionUuid && params.entityId),
    ...APOLLO_BASE_OPTIONS
  });

  return (
    <>
      <BaseHelmet page={endpointData?.getEndpoint?.name ?? 'Endpoint'} />
      <EntityDetailsHeader
        isLoading={loadingEndpoint}
        name={endpointData?.getEndpoint?.name}
        description={endpointData?.getEndpoint?.description}
        onModalOpen={setEndpointModalOpen}
        isEditable={isEditable}
      />

      <Divider borderColor="secondary" />
      <Box p="xxl">
        <H4 fontSize="lg" fontWeight="bold">
          Service
        </H4>
        <H4 mt="xs" color="secondary">
          {endpointData?.getEndpoint?.WebService?.name}
        </H4>
        <H4 mt="xs" color="secondary">
          {endpointData?.getEndpoint?.WebService?.schema.host}
        </H4>
      </Box>

      <Divider borderColor="secondary" />
      <Box p="xxl">
        <H4 fontSize="lg" fontWeight="bold">
          Used in flows
        </H4>
        <H4 mt="xs" color="secondary">
          This endpoint is used in the following flows.
        </H4>

        {infoData?.getEntityUsageInfo.flows.map(flow => (
          <FlexLayout
            key={flow.uuid || 'main'}
            justifyContent="space-between"
            borderRadius="small"
            bg="lightGrey"
            alignItems="center"
            p="sm"
            mt="lg"
          >
            <FlexLayout>
              <Icon src="play" mr="xs" color="primary" />
              <H4 color="primaryText" fontWeight="semiBold">
                {flow.name}
              </H4>
            </FlexLayout>
            <Link to={`../flows/${flow.uuid}`}>
              <Icon src="arrowForwardRounded" stroke="transparent" mr="xs" />
              Go to flow
            </Link>
          </FlexLayout>
        ))}
      </Box>

      <CreateEditEndpointModal
        versionUuid={params.versionUuid}
        isEdit
        isOpen={endpointModalOpen}
        editEndpoint={endpointData}
        setOpenState={state => setEndpointModalOpen(state)}
        onPackageCreated={() => {
          if (refetch) {
            refetch();
          }
        }}
      />
    </>
  );
};
